/* eslint-disable */
import { Injectable } from '@angular/core';
import { isPrimitive } from 'src/app/utils/misc.utils';
import { environment } from 'src/environments/environment';
import { MSRemoteLoggerCategory, MSRemoteLoggerData, MSRemoteLoggerPrimitive, MSRemoteLoggerSeverity } from '../adapters/remote-logger/utils/remote-logger.utils';
import { RemoteLoggerUseCase } from '../use-case/remote-logger/remote-logger.use-case';
import { DateTimeService } from './date-time/date-time.service';
import { UtilsService } from './utils.service';

declare let cordova: any;
type LoggerArgs = (MSRemoteLoggerPrimitive | MSRemoteLoggerPrimitive[] | MSRemoteLoggerData)[];

@Injectable({
  providedIn: 'root'
})
export class LoggerService {
  isDev = environment.production === false;
  maxLines = this.isDev ? 200 : 20;
  logs = [];

  constructor(
    private utils: UtilsService,
    private dateTime: DateTimeService,
    private remoteLogger: RemoteLoggerUseCase,
  ) {}

  private _log(loggerFn, args, canSuppress) {
    if (canSuppress && !this.isDev) {
      return;
    }
    const line = [];
    for (const a of args) {
      line.push(this.utils.toPrunedJson(a));
    }

    line.unshift(this.dateTime.getNowTime());
    loggerFn.apply(console, line);

    line.join(' ');
    this.logs.unshift(line);
    if (this.logs.length > this.maxLines) {
      this.logs.length = this.maxLines;
    }
  }

  public log(message: any, category: MSRemoteLoggerCategory, ...args: LoggerArgs) {
    this._log(console.log, [message, ...args], true);

    const condensed = this.condenseMessage(message, args);
    if (this.isDev) {
      this.remoteLogger.log(condensed.message, category, MSRemoteLoggerSeverity.DEBUG, ...condensed.remainingArgs);
    } else {
      const data = { data: condensed.remainingArgs } as MSRemoteLoggerData;
      this.remoteLogger.addBreadcrumbs(condensed.message, category, MSRemoteLoggerSeverity.LOG, data);
    }
  }

  public debug(message: any, category: MSRemoteLoggerCategory, ...args: LoggerArgs) {
    this._log(console.debug, [message, ...args], true);

    const condensed = this.condenseMessage(message, args);
    if (this.isDev) {
      this.remoteLogger.log(condensed.message, category, MSRemoteLoggerSeverity.DEBUG, ...condensed.remainingArgs);
    } else {
      const data = { data: condensed.remainingArgs } as MSRemoteLoggerData;
      this.remoteLogger.addBreadcrumbs(condensed.message, category, MSRemoteLoggerSeverity.DEBUG, data);
    }
  }

  public info(message: any, category: MSRemoteLoggerCategory, ...args: LoggerArgs) {
    this._log(console.info, [message, ...args], true);

    const condensed = this.condenseMessage(message, args);
    this.remoteLogger.log(condensed.message, category, MSRemoteLoggerSeverity.INFO, ...condensed.remainingArgs);
  }

  public warn(message: any, category: MSRemoteLoggerCategory, ...args: LoggerArgs) {
    this._log(console.warn, [message, ...args], false);

    const condensed = this.condenseMessage(message, args);
    this.remoteLogger.log(condensed.message, category, MSRemoteLoggerSeverity.WARN, ...condensed.remainingArgs);
  }

  public error(message: any, category: MSRemoteLoggerCategory, ...args: any[]) {
    this._log(console.error, [message, ...args], false);

    let condensed;
    if (args[0] && args[0].hasOwnProperty('message')) {
      condensed = this.condenseMessage(message, [args[0].message, ...args.slice(1)]);
    } else {
      condensed = this.condenseMessage(message, args);
    }

    this.remoteLogger.log(condensed.message, category, MSRemoteLoggerSeverity.ERROR, ...condensed.remainingArgs);
  }

  makeFailureLogger(msg) {
    const self = this;
    return function (...args) {
      self.warn('Failure: ', MSRemoteLoggerCategory.INIT, msg, ...args);
    };
  }

  makeSuccessLogger(msg) {
    const self = this;
    return function (...args) {
      self.log('Success', MSRemoteLoggerCategory.INIT, msg, ...args);
    };
  }

  private condenseMessage(message: string, args: LoggerArgs): { message: string, remainingArgs: LoggerArgs } {
    const remainingArgs = [];
    let newMessage = message;
    args.forEach(arg => {
      if (isPrimitive(arg)) {
        newMessage += ` ${arg}`;
      } else {
        remainingArgs.push(arg);
      }
    });

    return { message: newMessage, remainingArgs };
  }
}
